import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowRight, KeyRound, Mail, Lock, Building2, Sparkles, Star, Heart, Music2, Info, Check, X } from "lucide-react";
import { signIn, fetchAuthSession, getCurrentUser } from "@aws-amplify/auth";
import { AUTH_API, fetchWithAuth } from "../utils/api";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import ddLogo from './ui/dd.png';
import { getSubdomain, isConventionSubdomain, handleConventionAccess, redirectToMainDomain, handleSpecificConventionAccess } from '../utils/conventionService';

// Dance Particles for Auth Pages
const AuthDanceParticles = () => {
  const createParticle = (index) => {
    const baseX = Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200);
    const baseY = Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800);
    
    return { startX: baseX, startY: baseY };
  };

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-10">
      {[...Array(15)].map((_, i) => {
        const { startX, startY } = createParticle(i);
        
        return (
          <motion.div
            key={i}
            className="absolute"
            initial={{
              x: startX,
              y: startY,
              scale: 0,
              rotate: 0
            }}
            animate={{
              y: [null, startY - 200, startY - 400],
              x: [
                null, 
                startX + (Math.random() * 100 - 50),
                startX + (Math.random() * 150 - 75)
              ],
              scale: [0, 1, 0],
              rotate: [0, 360, 720],
              opacity: [0, 0.8, 0]
            }}
            transition={{
              duration: Math.random() * 4 + 6,
              repeat: Infinity,
              delay: Math.random() * 5,
              ease: "easeInOut"
            }}
          >
            {i % 4 === 0 && <Star className="w-3 h-3 text-pink-400 drop-shadow-lg" />}
            {i % 4 === 1 && <Heart className="w-3 h-3 text-purple-400 drop-shadow-lg" />}
            {i % 4 === 2 && <Music2 className="w-3 h-3 text-orange-400 drop-shadow-lg" />}
            {i % 4 === 3 && <div className="w-2 h-2 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full drop-shadow-lg" />}
          </motion.div>
        );
      })}
    </div>
  );
};

// Stage Background for Auth
const AuthStageBackground = () => {
  return (
    <div className="fixed inset-0 -z-10">
      {/* Base gradient matching homepage */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-pink-900 to-orange-900" />
      
      {/* Animated spotlight effects */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            "radial-gradient(circle at 30% 40%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)",
            "radial-gradient(circle at 70% 60%, rgba(168, 85, 247, 0.4) 0%, transparent 50%)",
            "radial-gradient(circle at 50% 30%, rgba(251, 146, 60, 0.3) 0%, transparent 50%)",
            "radial-gradient(circle at 30% 40%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)"
          ]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
      />
      
      {/* Stage floor reflection */}
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
    </div>
  );
};

// Stage Card Component for Auth
const AuthStageCard = ({ children, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{ 
        scale: 1.02,
        boxShadow: "0 25px 50px -12px rgba(236, 72, 153, 0.4)",
      }}
      className={`relative bg-black/40 backdrop-blur-xl border border-white/20 rounded-3xl shadow-pink-500/30 ${className}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />
      <div className="relative">{children}</div>
    </motion.div>
  );
};

// Rhythm Pulse for Auth Elements
const AuthRhythmPulse = ({ children, delay = 0 }) => {
  return (
    <motion.div
      animate={{
        scale: [1, 1.02, 1],
        filter: ["brightness(1)", "brightness(1.1)", "brightness(1)"]
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        delay,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );
};

// Auth Token Utility
const getAuthToken = async () => {
  const session = await fetchAuthSession();
  if (!session?.tokens?.idToken) {
    throw new Error("No valid authentication token found");
  }
  return session.tokens.idToken.toString();
};

// Password Requirements Tooltip Component
const PasswordRequirementsTooltip = ({ show, password = "" }) => {
  const requirements = [
    { 
      text: "At least 8 characters", 
      met: password.length >= 8 
    },
    { 
      text: "At least one uppercase letter (A-Z)", 
      met: /[A-Z]/.test(password) 
    },
    { 
      text: "At least one lowercase letter (a-z)", 
      met: /[a-z]/.test(password) 
    },
    { 
      text: "At least one number (0-9)", 
      met: /[0-9]/.test(password) 
    },
    { 
      text: "At least one special character (!@#$%^&*)", 
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) 
    }
  ];

  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="absolute top-full left-0 mt-1 p-4 bg-black/90 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl z-50 min-w-[280px]"
    >
      <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
        <Lock className="w-4 h-4 mr-2 text-pink-400" />
        Password Requirements
      </h4>
      <ul className="space-y-2">
        {requirements.map((req, index) => (
          <motion.li
            key={index}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2, delay: index * 0.05 }}
            className="flex items-center text-xs"
          >
            {req.met ? (
              <Check className="w-3 h-3 mr-2 text-green-400 flex-shrink-0" />
            ) : (
              <X className="w-3 h-3 mr-2 text-red-400 flex-shrink-0" />
            )}
            <span className={req.met ? "text-green-300" : "text-red-300"}>
              {req.text}
            </span>
          </motion.li>
        ))}
      </ul>
    </motion.div>
  );
};

// Password Input with Requirements
const PasswordInput = ({ 
  label, 
  value, 
  onChange, 
  placeholder, 
  showRequirements = false,
  preventAutofill = false,
  className = "",
  ...props 
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Autofill prevention attributes
  const autofillProps = preventAutofill ? {
    autoComplete: "new-password",
    name: `new-password-${Math.random().toString(36).substr(2, 9)}`, // Random name to prevent autofill
    "data-lpignore": "true",
    "data-form-type": "other"
  } : {};

  return (
    <div>
      <label className="block text-sm font-semibold text-white/90 mb-3 flex items-center">
        {label}
        {showRequirements && (
          <div className="relative ml-2">
            <motion.button
              type="button"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="p-1 rounded-full bg-pink-500/20 hover:bg-pink-500/30 transition-colors"
            >
              <Info className="w-3 h-3 text-pink-400" />
            </motion.button>
            <AnimatePresence>
              <PasswordRequirementsTooltip 
                show={showTooltip} 
                password={value} 
              />
            </AnimatePresence>
          </div>
        )}
      </label>
      <div className="relative">
        <Lock className="absolute left-4 top-4 h-5 w-5 text-pink-400/70" />
        <input
          type="password"
          value={value}
          onChange={onChange}
          className={`w-full pl-12 pr-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30 ${className}`}
          placeholder={placeholder}
          {...autofillProps}
          {...props}
        />
      </div>
    </div>
  );
};

// Utility function to check password requirements
const validatePassword = (password) => {
  const requirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
  };
  
  const isValid = Object.values(requirements).every(Boolean);
  return { isValid, requirements };
};

// Function to generate helpful error messages
const getPasswordErrorMessage = (error) => {
  const errorMessage = error.toLowerCase();
  
  if (errorMessage.includes('password') && (errorMessage.includes('policy') || errorMessage.includes('requirement') || errorMessage.includes('invalid'))) {
    return "Password must be at least 8 characters and include uppercase, lowercase, number, and special character (!@#$%^&*).";
  }
  
  if (errorMessage.includes('temporary password') || errorMessage.includes('expired')) {
    return "Your temporary password has expired. Please request a new password reset.";
  }
  
  if (errorMessage.includes('attempt') && errorMessage.includes('limit')) {
    return "Too many failed attempts. Please wait a few minutes before trying again.";
  }
  
  if (errorMessage.includes('user') && errorMessage.includes('not') && errorMessage.includes('found')) {
    return "No account found with this email address. Please check your email or sign up for a new account.";
  }
  
  if (errorMessage.includes('incorrect') || errorMessage.includes('wrong')) {
    return "Incorrect email or password. Please try again.";
  }
  
  // Return original error if no specific pattern matched
  return error;
};

function AuthSystem({ initialAuthState = "signIn" }) {
  const navigate = useNavigate();
  const { isAuthenticated, role, isLoading: authLoading, refreshUserData, user } = useAuth();
  const [authState, setAuthState] = useState(initialAuthState);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    code: "",
    newPassword: "",
    confirmPassword: "",
    role: "",
    firstName: "",
    lastName: "",
    studioName: "",
    studioAddress: "",
    studioPhone: "",
  });
  const [authError, setAuthError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  
  // Check for convention parameter
  const [searchParams] = useSearchParams();
  const conventionParam = searchParams.get("convention");


  // Check authentication status using centralized auth context
  useEffect(() => {
    // Don't redirect if we're still loading auth state
    if (authLoading) return;
    
    
    // If AuthContext thinks user is authenticated but we're on auth page, 
    // this might be a stale state - trigger an immediate auth check
    if (isAuthenticated && window.location.pathname === '/auth') {
      refreshUserData();
    }
    
    // If user is authenticated, handle convention access and redirect
    // But only if they didn't just sign out (which would briefly show them as authenticated)
    if (isAuthenticated && role) {
      
      const handleAuthenticatedUserRedirect = async () => {
        // If there's a convention parameter, add convention access
        if (conventionParam) {
          try {
            // Pass user's current conventions to avoid duplicate API calls
            const userConventions = user?.conventions || [];
            await handleSpecificConventionAccess(conventionParam, userConventions);
          } catch (error) {
            console.error('Failed to process convention access:', error);
            // Continue with normal redirect even if convention access fails
          }
        }
        
        // Normal redirect logic - fallback to Cognito groups if role not loaded yet
        let userRole = role;
        
        // If role is not loaded from AuthContext yet, check Cognito groups directly
        if (!userRole) {
          try {
            const session = await fetchAuthSession();
            const accessToken = session.tokens?.accessToken;
            const payload = accessToken?.payload;
            const groups = payload["cognito:groups"] || [];
            
            if (groups.includes("Admin")) {
              userRole = "Admin";
            } else if (groups.includes("Judge")) {
              userRole = "Judge";
            }
          } catch (error) {
            console.error("Error checking Cognito groups:", error);
          }
        }
        
        if (userRole === 'Admin') {
          navigate("/admin/dashboard");
        } else if (userRole === 'Judge') {
          navigate("/admin/limited-dashboard");
        } else {
          // Route all other users to user dashboard
          navigate("/user/dashboard");
        }
      };
      
      handleAuthenticatedUserRedirect();
    }
  }, [isAuthenticated, role, authLoading, navigate, user, conventionParam]);

  const ROLE_OPTIONS = [
    { value: "studioOwner", label: "Studio Owner" },
    { value: "teacher", label: "Teacher" },
    { value: "dancer", label: "Dancer" },
    { value: "parent", label: "Parent" },
  ];

  const updateFormData = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    
    // Clear auth error when user starts typing
    setAuthError("");
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    setAuthError("");
    
  
    try {
      const signInOutput = await signIn({
        username: formData.email,
        password: formData.password,
      });


      if (signInOutput.isSignedIn) {
        // Refresh user data in the auth context
        await refreshUserData();
        
        // The useEffect above will handle the redirect and convention access
        // after the auth context updates with the new user data
      }
    } catch (error) {
      console.error("Error signing in:", error);
      
      // Handle the case where user is already signed in
      if (error.message?.includes('UserAlreadyAuthenticatedException') || 
          error.message?.includes('There is already a signed in user')) {
        try {
          await refreshUserData();
          // Don't set error, let the redirect happen
        } catch (refreshError) {
          console.error('Error refreshing auth for signed in user:', refreshError);
          setAuthError("Please sign out and try again.");
        }
      } else {
        const friendlyError = getPasswordErrorMessage(error.message);
        setAuthError(friendlyError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Update handleSignUp
  const handleSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError("");

    if (formData.password !== formData.confirmPassword) {
      setAuthError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    // Validate password requirements before submitting
    const { isValid } = validatePassword(formData.password);
    if (!isValid) {
      setAuthError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character (!@#$%^&*).");
      setIsLoading(false);
      return;
    }

    try {
      // Build the registration payload
      const registerPayload = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
      };

      // Only add studio fields if the role is studioOwner
      if (formData.role === 'studioOwner') {
        registerPayload.studioName = formData.studioName;
        registerPayload.studioAddress = formData.studioAddress;
        registerPayload.studioContactInfo = formData.studioPhone;
      }

      const response = await fetch(`${AUTH_API}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registerPayload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to sign up');
      }

      setSuccessMessage("Please check your email for the verification code.");
      setAuthState("confirmSignUp");
    } catch (error) {
      console.error("Sign up failed:", error);
      const friendlyError = getPasswordErrorMessage(error.message || "Failed to sign up.");
      setAuthError(friendlyError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError("");

    try {
      const response = await fetch(`${AUTH_API}/confirm-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          code: formData.code
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to confirm email');
      }

      setSuccessMessage("Email verified successfully! Please sign in.");
      setAuthState("signIn");
    } catch (error) {
      console.error("Confirmation failed:", error);
      setAuthError(error.message || "Failed to confirm sign up.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError("");

    try {
      const response = await fetch(`${AUTH_API}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to request password reset');
      }

      setSuccessMessage("Password reset code sent to your email.");
      setAuthState("resetPassword");
    } catch (error) {
      console.error("Password reset request failed:", error);
      const friendlyError = getPasswordErrorMessage(error.message || "Failed to request password reset.");
      setAuthError(friendlyError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setAuthError("");

    if (formData.newPassword !== formData.confirmPassword) {
      setAuthError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    // Validate password requirements before submitting
    const { isValid } = validatePassword(formData.newPassword);
    if (!isValid) {
      setAuthError("Password must be at least 8 characters and include uppercase, lowercase, number, and special character (!@#$%^&*).");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${AUTH_API}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          code: formData.code,
          password: formData.newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to reset password');
      }

      setSuccessMessage("Password reset successful! Please sign in.");
      setAuthState("signIn");
    } catch (error) {
      console.error("Password reset failed:", error);
      const friendlyError = getPasswordErrorMessage(error.message || "Failed to reset password.");
      setAuthError(friendlyError);
    } finally {
      setIsLoading(false);
    }
  };

  // Your existing JSX remains exactly the same
   return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <AuthStageBackground />
      <AuthDanceParticles />

      {/* Header */}
      <motion.div 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-0 w-full bg-black/80 backdrop-blur-2xl border-b border-pink-500/30 z-50"
      >
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center space-x-3 group w-fit">
            <motion.div 
              whileHover={{ scale: 1.1, rotate: 10 }}
              className="relative"
            >
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center border border-pink-400/50 shadow-lg shadow-pink-500/25">
                <img 
                  src={ddLogo}
                  alt="Dance Digital"
                  className="w-7 h-7 object-contain filter brightness-0 invert"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-pink-400 to-purple-500 rounded-2xl blur-lg opacity-60 group-hover:opacity-80 transition-opacity" />
            </motion.div>
            <span className="text-2xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent">
              Dance Digital
            </span>
          </Link>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24 pb-12 min-h-screen">
        <div className="relative w-full max-w-md z-20">
          <AuthStageCard className="p-8">
            {/* Logo and Title */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center mb-8"
            >
              <AuthRhythmPulse>
                <div className="p-4 bg-gradient-to-br from-pink-500/20 to-purple-500/20 rounded-2xl border border-pink-400/40 backdrop-blur-xl">
                  <KeyRound className="w-8 h-8 text-pink-300" />
                </div>
              </AuthRhythmPulse>
            </motion.div>

            {/* Auth State Title */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-center mb-8"
            >
              {authState === "signIn" && (
                <>
                  <AuthRhythmPulse delay={0.5}>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent mb-3">
                      Welcome Back
                    </h2>
                  </AuthRhythmPulse>
                  <p className="text-white/70 text-lg">
                    Sign in to your account to continue
                  </p>
                </>
              )}
              {authState === "signUp" && (
                <>
                  <AuthRhythmPulse delay={0.5}>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent mb-3">
                      Create Account
                    </h2>
                  </AuthRhythmPulse>
                  <p className="text-white/70 text-lg">Join us to get started</p>
                </>
              )}
              {authState === "confirmSignUp" && (
                <>
                  <AuthRhythmPulse delay={0.5}>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent mb-3">
                      Verify Email
                    </h2>
                  </AuthRhythmPulse>
                  <p className="text-white/70 text-lg">
                    Enter the code sent to your email
                  </p>
                </>
              )}
              {authState === "forgotPassword" && (
                <>
                  <AuthRhythmPulse delay={0.5}>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent mb-3">
                      Reset Password
                    </h2>
                  </AuthRhythmPulse>
                  <p className="text-white/70 text-lg">
                    We'll send you a reset code
                  </p>
                </>
              )}
              {authState === "resetPassword" && (
                <>
                  <AuthRhythmPulse delay={0.5}>
                    <h2 className="text-3xl font-bold bg-gradient-to-r from-pink-300 via-purple-300 to-orange-300 bg-clip-text text-transparent mb-3">
                      New Password
                    </h2>
                  </AuthRhythmPulse>
                  <p className="text-white/70 text-lg">
                    Enter the code and your new password
                  </p>
                </>
              )}
            </motion.div>

            {/* Error Message */}
            <AnimatePresence>
              {authError && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="mb-6 p-4 bg-red-500/10 border border-red-400/30 rounded-2xl backdrop-blur-xl"
                >
                  <p className="text-sm text-red-300 text-center font-medium">{authError}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Success Message */}
            <AnimatePresence>
              {successMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="mb-6 p-4 bg-green-500/10 border border-green-400/30 rounded-2xl backdrop-blur-xl"
                >
                  <p className="text-sm text-green-300 text-center font-medium">
                    {successMessage}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Auth Forms */}
            {authState === "signIn" && (
              <motion.form 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                onSubmit={handleSignIn} 
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-3">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-4 h-5 w-5 text-pink-400/70" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData("email", e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-3">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-4 h-5 w-5 text-pink-400/70" />
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) =>
                        updateFormData("password", e.target.value)
                      }
                      className="w-full pl-12 pr-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setAuthState("forgotPassword")}
                    className="text-sm text-pink-300 hover:text-pink-200 transition-colors font-medium"
                  >
                    Forgot password?
                  </motion.button>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 font-bold text-lg border border-pink-400/50 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                  {isLoading ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full" 
                    />
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </motion.button>

                <p className="text-center text-sm text-white/60">
                  Don't have an account?{" "}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setAuthState("signUp")}
                    className="text-pink-300 hover:text-pink-200 transition-colors font-semibold"
                  >
                    Sign up now
                  </motion.button>
                </p>
              </motion.form>
            )}

            {authState === "signUp" && (
              <motion.form 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                onSubmit={handleSignUp} 
                className="space-y-6"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-white/90 mb-3">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) =>
                        updateFormData("firstName", e.target.value)
                      }
                      className="w-full px-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                      placeholder="First name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/90 mb-3">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) =>
                        updateFormData("lastName", e.target.value)
                      }
                      className="w-full px-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-3">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-4 h-5 w-5 text-pink-400/70" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData("email", e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-3">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => updateFormData("role", e.target.value)}
                    className="w-full px-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                    required
                  >
                    <option value="" className="bg-black/90">
                      Select a role
                    </option>
                    {ROLE_OPTIONS.map((role) => (
                      <option
                        key={role.value}
                        value={role.value}
                        className="bg-black/90"
                      >
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.role === "studioOwner" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="space-y-6"
                  >
                    <div>
                      <label className="block text-sm font-semibold text-white/90 mb-3">
                        Studio Name
                      </label>
                      <div className="relative">
                        <Building2 className="absolute left-4 top-4 h-5 w-5 text-orange-400/70" />
                        <input
                          type="text"
                          value={formData.studioName}
                          onChange={(e) =>
                            updateFormData("studioName", e.target.value)
                          }
                          className="w-full pl-12 pr-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-400/50 transition-all duration-300 hover:border-white/30"
                          placeholder="Enter studio name"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white/90 mb-3">
                        Studio Address
                      </label>
                      <input
                        type="text"
                        value={formData.studioAddress}
                        onChange={(e) =>
                          updateFormData("studioAddress", e.target.value)
                        }
                        className="w-full px-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-400/50 transition-all duration-300 hover:border-white/30"
                        placeholder="Enter studio address"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-white/90 mb-3">
                        Studio Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.studioPhone}
                        onChange={(e) =>
                          updateFormData("studioPhone", e.target.value)
                        }
                        className="w-full px-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-400/50 transition-all duration-300 hover:border-white/30"
                        placeholder="Enter studio phone"
                        required
                      />
                    </div>
                  </motion.div>
                )}

                <PasswordInput
                  label="Password"
                  value={formData.password}
                  onChange={(e) => updateFormData("password", e.target.value)}
                  placeholder="Create a password"
                  showRequirements={true}
                  required
                />

                <PasswordInput
                  label="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData("confirmPassword", e.target.value)}
                  placeholder="Confirm your password"
                  showRequirements={true}
                  required
                />

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 font-bold text-lg border border-pink-400/50 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                  {isLoading ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full" 
                    />
                  ) : (
                    <>
                      Sign Up
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </>
                  )}
                </motion.button>

                <p className="text-center text-sm text-white/60">
                  Already have an account?{" "}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setAuthState("signIn")}
                    className="text-pink-300 hover:text-pink-200 transition-colors font-semibold"
                  >
                    Sign in instead
                  </motion.button>
                </p>
              </motion.form>
            )}
            {/* Confirm Sign Up Form */}
            {authState === "confirmSignUp" && (
              <motion.form 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                onSubmit={handleConfirmSignUp} 
                className="space-y-6"
                autoComplete="off"
              >
                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-3">
                    Verification Code
                  </label>
                  <input
                    type="text"
                    name="verification-code"
                    value={formData.code}
                    onChange={(e) => updateFormData("code", e.target.value)}
                    className="w-full px-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                    placeholder="Enter verification code"
                    autoComplete="one-time-code"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="6"
                    data-lpignore="true"
                    data-form-type="other"
                    required
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 font-bold text-lg border border-pink-400/50 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                  {isLoading ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full" 
                    />
                  ) : (
                    "Verify Email"
                  )}
                </motion.button>

                <p className="text-center text-sm text-white/60">
                  Didn't receive the code?{" "}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setAuthState("signUp")}
                    className="text-pink-300 hover:text-pink-200 transition-colors font-semibold"
                  >
                    Go back and try again
                  </motion.button>
                </p>
              </motion.form>
            )}

            {/* Forgot Password Form */}
            {authState === "forgotPassword" && (
              <motion.form 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                onSubmit={handleForgotPassword} 
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-3">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-4 h-5 w-5 text-pink-400/70" />
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData("email", e.target.value)}
                      className="w-full pl-12 pr-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 font-bold text-lg border border-pink-400/50 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                  {isLoading ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full" 
                    />
                  ) : (
                    "Send Reset Code"
                  )}
                </motion.button>

                <p className="text-center text-sm text-white/60">
                  Remember your password?{" "}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setAuthState("signIn")}
                    className="text-pink-300 hover:text-pink-200 transition-colors font-semibold"
                  >
                    Sign in instead
                  </motion.button>
                </p>
              </motion.form>
            )}

            {/* Reset Password Form */}
            {authState === "resetPassword" && (
              <motion.form 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                onSubmit={handleResetPassword} 
                className="space-y-6"
                autoComplete="off"
              >
                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-3">
                    Reset Code
                  </label>
                  <input
                    type="text"
                    name="reset-code"
                    value={formData.code}
                    onChange={(e) => updateFormData("code", e.target.value)}
                    className="w-full px-4 py-4 bg-black/30 backdrop-blur-xl border border-white/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-400/50 transition-all duration-300 hover:border-white/30"
                    placeholder="Enter reset code"
                    autoComplete="one-time-code"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength="6"
                    data-lpignore="true"
                    data-form-type="other"
                    required
                  />
                </div>

                <PasswordInput
                  label="New Password"
                  value={formData.newPassword}
                  onChange={(e) => updateFormData("newPassword", e.target.value)}
                  placeholder="Create new password"
                  showRequirements={true}
                  preventAutofill={true}
                  required
                />

                <PasswordInput
                  label="Confirm New Password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData("confirmPassword", e.target.value)}
                  placeholder="Confirm new password"
                  showRequirements={true}
                  preventAutofill={true}
                  required
                />

                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isLoading}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-2xl hover:shadow-2xl hover:shadow-pink-500/30 transition-all duration-300 disabled:opacity-50 disabled:hover:scale-100 font-bold text-lg border border-pink-400/50 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-400 to-purple-500 opacity-0 group-hover:opacity-20 transition-opacity" />
                  {isLoading ? (
                    <motion.div 
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full" 
                    />
                  ) : (
                    "Reset Password"
                  )}
                </motion.button>

                <p className="text-center text-sm text-white/60">
                  Remember your password?{" "}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={() => setAuthState("signIn")}
                    className="text-pink-300 hover:text-pink-200 transition-colors font-semibold"
                  >
                    Sign in instead
                  </motion.button>
                </p>
              </motion.form>
            )}
          </AuthStageCard>
        </div>
      </div>
    </div>
  );
}

export default AuthSystem;
